import React, { FC, useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import Button from '../../../../components/common/Button';
import Input from '../../../../components/common/Input';
import Accordion from '../../../../components/elements/Accordion';
import Confirmation from '../../../../components/elements/Confirmation';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useModal } from '../../../../hooks/useModal';
import { useAppSelector } from '../../../../hooks/useSelector';
import { IQuickDuelState } from '../../../../lib/interfaces/IQuickDuel';
import { getQuickDuel } from '../../../../store/selectors/settings.selector';
import { QuickDuelInitialState, settingsActions } from '../../../../store/slices/settings.slice';
import { teamActions } from '../../../../store/slices/team.slice';

import {
  RESET_SETTINGS_ABORT_TEXT,
  RESET_SETTINGS_CONFIRM_TEXT,
  RESET_SETTINGS_CONFIRMATION,
} from './config';
import * as S from './styles';

export interface IQuickDuelFormProps {
  toggleFlyout: () => void;
}

const QuickDuelForm: FC<IQuickDuelFormProps> = ({ toggleFlyout }) => {
  const dispatch = useAppDispatch();
  const [params, setParams] = useSearchParams();
  const initialState = useAppSelector(getQuickDuel);
  const { isActive, toggleHandler: toggleConfirmation } = useModal({ handler: toggleFlyout });
  const { register, handleSubmit, reset } = useForm<IQuickDuelState>({
    defaultValues: initialState,
  });

  const onSubmit: SubmitHandler<IQuickDuelState> = useCallback(
    (data) => {
      dispatch(settingsActions.setQuickDuel(data));
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player1',
          settings: { min: data.roundTime.min, sec: data.roundTime.sec },
        }),
      );
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player2',
          settings: { min: data.roundTime.min, sec: data.roundTime.sec },
        }),
      );
      dispatch(teamActions.updateTimerKeys());
      params.set('game_mode', 'quick game');
      setParams(params);
      toggleFlyout();
    },
    [dispatch, params, setParams, toggleFlyout],
  );

  const onConfirmReset = useCallback(() => {
    dispatch(settingsActions.resetQuickDuel());
    toggleConfirmation();
    dispatch(teamActions.updateTimerKeys());
    reset(QuickDuelInitialState);
  }, [dispatch, reset, toggleConfirmation]);

  return (
    <>
      <S.Form onSubmit={handleSubmit(onSubmit)}>
        <Accordion title="game timer">
          <S.AccordionElementsContainer>
            <Input
              variant="time"
              label="round time"
              minProps={{
                ...register('roundTime.min', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
              secProps={{
                ...register('roundTime.sec', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
            />
            <Input
              variant="toggle"
              label="End of Round Sound"
              {...register('isEnabledEndOfRoundSound')}
              defaultChecked={initialState.isEnabledEndOfRoundSound}
            />
          </S.AccordionElementsContainer>
        </Accordion>

        <S.Line />

        <Accordion
          title="Voters' timer"
          // $isDisabledPadding
        >
          <S.AccordionElementsContainer>
            <Input
              variant="time"
              label="Voter's time"
              minProps={{
                ...register('votersTimer.pauseTime.min', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
              secProps={{
                ...register('votersTimer.pauseTime.sec', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
            />

            <Input
              variant="toggle"
              label="Start blinking"
              {...register('votersTimer.isStartBlinking')}
              defaultChecked={initialState.votersTimer.isStartBlinking}
            />

            <Input
              variant="toggle"
              label="End of Pause Sound"
              {...register('votersTimer.isEnabledEndOfPauseSound')}
              defaultChecked={initialState.votersTimer.isEnabledEndOfPauseSound}
            />
          </S.AccordionElementsContainer>
        </Accordion>

        <S.ButtonsContainer>
          <Button
            variant="outline"
            $size="large"
            type="button"
            onClick={toggleConfirmation}
          >
            reset settings
          </Button>
          <Button
            variant="primary"
            $size="large"
            type="submit"
          >
            apply
          </Button>
        </S.ButtonsContainer>
      </S.Form>
      <Confirmation
        description={RESET_SETTINGS_CONFIRMATION}
        confirmText={RESET_SETTINGS_CONFIRM_TEXT}
        abortText={RESET_SETTINGS_ABORT_TEXT}
        title="reset settings"
        isActive={isActive}
        onConfirm={onConfirmReset}
        onClose={toggleConfirmation}
        onAbort={toggleConfirmation}
      />
    </>
  );
};

export default QuickDuelForm;
